import React, { useState, useEffect, useCallback } from "react";
import { getRequest, patchRequest, postRequest } from "../../utils/httpRequest";
import { HelpTitle } from "../AdminHeaderHelp";

// Admin page to view + edit the configurable Agentic Payment fees (backed by
// lp-api via Rails page_action proxies, no API key in the browser).
const colors = {
  primary: "#2e3b61",
  accent: "#e58734",
  accentDark: "#d77a2a",
  success: "#28a745",
  danger: "#dc3545",
  white: "#fff",
  gray100: "#f8f9fa",
  gray200: "#f0f2f5",
  gray500: "#999",
  gray600: "#666",
  gray700: "#333",
  border: "#dcdfe6",
};

const BASE = "/admin/agentic_payment_fees";

const isPercent = (unit) => {
  const u = (unit || "").toLowerCase();
  return u.includes("percent") || u === "%";
};

// percent → "0.3%" (value is a human percentage); flat → "$2.50" / "2.50 NGN".
const formatValue = (fee) => {
  const n = Number(fee.value);
  if (!Number.isFinite(n)) return String(fee.value ?? "-");
  if (isPercent(fee.unit)) {
    return `${n}%`;
  }
  const cur = (fee.currency || "USD").toUpperCase();
  const amount = n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return cur === "USD" ? `$${amount}` : `${amount} ${cur}`;
};


// Which payment flow the fee applies to. lp-api doesn't return an explicit
// flow field, so derive it from the slug (with friendly labels for the known
// seeded fees, humanized fallback otherwise). Uses fee.flow if the backend
// ever adds it.
const FLOW_LABELS = {
  deposit_fee: "Deposits",
  withdraw_fee: "Withdrawals",
  transfer_fee: "Transfers",
  trade_settlement_fee: "Trade settlement",
  offramp_one_time_fee: "Off-ramp",
  onramp_fee: "On-ramp",
  owlpay_commission: "OwlPay transfers (our revenue)",
  owlpay_take_rate: "OwlPay transfers (their cost)",
  owlpay_processing_fee: "OwlPay transfers (their cost)",
  // Spelled out so the brand names are not humanised as "Moneygram".
  moneygram_offramp_fee: "MoneyGram withdrawals",
  moneygram_onramp_fee: "MoneyGram deposits",
  yellowcard_offramp_commission: "YellowCard withdrawals",
};

// Harbor round-up (markup) fee family, charged to the customer = OwlPay's
// harbor cost rounded UP to a configurable step; the difference is our revenue.
const HARBOR_PREFIX = "owlpay_harbor_roundup_step";
const isHarborRoundup = (fee) => (fee.slug || "").startsWith(HARBOR_PREFIX);

// owlpay_* (excluding the harbor round-up family) are the OwlPay partner rows, // grouped under their own heading, editable (owlpay_commission is our revenue).
const isInternalFee = (fee) => (fee.slug || "").startsWith("owlpay_") && !isHarborRoundup(fee);

// Fees are tabbed per provider, new providers (YellowCard, MoneyGram, …) route
// in automatically by slug prefix; anything without a provider prefix is a core
// Platform fee.
const TABS = [
  { key: "platform", label: "Platform" },
  { key: "owlpay", label: "OwlPay" },
  { key: "yellowcard", label: "YellowCard" },
  { key: "moneygram", label: "MoneyGram" },
];
const providerOf = (fee) => {
  const s = fee.slug || "";
  if (s.startsWith("owlpay_")) return "owlpay";
  if (s.startsWith("yellowcard_")) return "yellowcard";
  if (s.startsWith("moneygram_")) return "moneygram";
  return "platform";
};

// OwlPay payment methods (for the "Add per-method step" dropdown).
const KNOWN_METHODS = [
  "ACH Push", "RTP", "Domestic Wire", "International Wire", "Debit Card", "SEPA",
  "SEPA instant", "Faster Payments", "CLABE (SPEI)", "PIX", "Local Bank Transfer", "Wire",
];

// method → slug suffix: lower-case, every non-alphanumeric run collapsed to "_".
const normalizeMethod = (m) => (m || "").toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");
const METHOD_BY_SUFFIX = KNOWN_METHODS.reduce((acc, m) => {
  acc[normalizeMethod(m)] = m;
  return acc;
}, {});

// Human method name for a harbor round-up row, derived from its slug suffix.
const harborMethodLabel = (fee) => {
  if (fee.slug === HARBOR_PREFIX) return "Global (all methods)";
  const suffix = (fee.slug || "").replace(`${HARBOR_PREFIX}_`, "");
  return (
    METHOD_BY_SUFFIX[suffix] ||
    suffix.split("_").filter(Boolean).map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ")
  );
};

const flowLabel = (fee) => {
  if (isHarborRoundup(fee)) return harborMethodLabel(fee);
  if (fee.flow) return fee.flow;
  if (FLOW_LABELS[fee.slug]) return FLOW_LABELS[fee.slug];
  const derived = (fee.slug || "")
    .replace(/_?(one_time_)?fee$/, "")
    .split("_")
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
  return derived || "-";
};

// Prettify an OwlPay method key for the harbor breakdown (wire → "Wire",
// ach_push → "ACH Push", fedwire → "Fedwire").
const prettyMethod = (method) => {
  const key = normalizeMethod(method);
  return (
    METHOD_BY_SUFFIX[key] ||
    key.split("_").filter(Boolean).map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ") ||
    String(method || "-")
  );
};

const usd = (v) => {
  const n = Number(v);
  return Number.isFinite(n)
    ? `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    : "-";
};

const formatWhen = (iso) => {
  if (!iso) return "-";
  try {
    return new Intl.DateTimeFormat(undefined, {
      day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
    }).format(new Date(iso));
  } catch {
    return iso;
  }
};

const AgenticPaymentFees = () => {
  const [fees, setFees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [modal, setModal] = useState({ open: false, fee: null });
  const [form, setForm] = useState({ value: "", unit: "percent", active: true, description: "" });
  const [saving, setSaving] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState({ method: "", value: "", description: "" });
  const [creating, setCreating] = useState(false);
  const [harborMarkup, setHarborMarkup] = useState([]);
  const [activeTab, setActiveTab] = useState("platform");

  const showToast = useCallback((message, type = "success") => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  }, []);

  const fetchFees = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getRequest(`${BASE}/fees`);
      if (res && res.success && Array.isArray(res.data)) {
        // Render every fee the endpoint returns; owlpay_* are grouped separately
        // in the table (they're editable, owlpay_commission is our revenue).
        setFees(res.data);
      } else {
        throw new Error((res && res.error) || "Failed to load fees");
      }
    } catch (err) {
      console.error(err);
      const msg = (err && err.data && err.data.error) || err.message || "Request failed";
      showToast("Failed to load fees: " + msg, "danger");
    } finally {
      setLoading(false);
    }

    // Harbor economics breakdown, independent of the fee list; a failure just
    // leaves the breakdown table empty.
    try {
      const hm = await getRequest(`${BASE}/harbor_markup`);
      setHarborMarkup(hm && hm.success && Array.isArray(hm.data) ? hm.data : []);
    } catch (err) {
      console.error(err);
      setHarborMarkup([]);
    }
  }, [showToast]);

  useEffect(() => {
    fetchFees();
  }, [fetchFees]);

  const openEdit = (fee) => {
    setForm({
      value: String(fee.value ?? ""),
      unit: isPercent(fee.unit) ? "percent" : "flat",
      active: fee.active !== false,
      description: fee.description || "",
    });
    setModal({ open: true, fee });
  };

  const closeEdit = () => {
    if (!saving) setModal({ open: false, fee: null });
  };

  const save = async () => {
    const fee = modal.fee;
    if (!fee) return;
    const value = parseFloat(form.value);
    if (!Number.isFinite(value) || value < 0) {
      showToast("Enter a valid, non-negative value.", "warning");
      return;
    }
    if (form.unit === "percent" && value > 100) {
      showToast("A percentage fee must be between 0 and 100.", "warning");
      return;
    }
    setSaving(true);
    try {
      const res = await patchRequest(`${BASE}/update_fee`, {
        slug: fee.slug,
        value,
        unit: form.unit,
        active: form.active,
        description: form.description,
      });
      if (res && res.success) {
        showToast(`"${fee.name}" updated.`, "success");
        setModal({ open: false, fee: null });
        fetchFees();
      } else {
        throw new Error((res && res.error) || "Update failed");
      }
    } catch (err) {
      console.error(err);
      const msg = (err && err.data && err.data.error) || err.message || "Request failed";
      showToast("Update failed: " + msg, "danger");
    } finally {
      setSaving(false);
    }
  };

  const submitAdd = async () => {
    const method = addForm.method.trim();
    const value = parseFloat(addForm.value);
    if (!method) {
      showToast("Choose or enter a payment method.", "warning");
      return;
    }
    if (!Number.isFinite(value) || value < 0) {
      showToast("Enter a valid round-up step (≥ 0).", "warning");
      return;
    }
    const slug = `${HARBOR_PREFIX}_${normalizeMethod(method)}`;
    setCreating(true);
    try {
      const res = await postRequest(`${BASE}/create_fee`, {
        slug,
        name: `OwlPay harbor round-up, ${method}`,
        value,
        unit: "flat",
        currency: "USD",
        description: addForm.description.trim() || `Round-up step for the ${method} method`,
      });
      if (res && res.success) {
        showToast(`Round-up step for "${method}" added.`, "success");
        setAddOpen(false);
        setAddForm({ method: "", value: "", description: "" });
        fetchFees();
      } else {
        throw new Error((res && res.error) || "Create failed");
      }
    } catch (err) {
      console.error(err);
      if (err && err.status === 409) {
        showToast("A step for this method already exists.", "danger");
      } else {
        const msg = (err && err.data && err.data.error) || err.message || "Request failed";
        showToast("Failed to add step: " + msg, "danger");
      }
    } finally {
      setCreating(false);
    }
  };

  const th = { textAlign: "left", padding: "12px", borderBottom: `2px solid ${colors.border}`, fontSize: "12px", textTransform: "uppercase", color: colors.gray600, letterSpacing: "0.5px" };
  const td = { padding: "12px", borderBottom: `1px solid ${colors.gray200}`, fontSize: "14px", color: colors.gray700 };
  const btn = (bg, disabled) => ({
    background: bg,
    color: colors.white,
    border: "none",
    borderRadius: "6px",
    padding: "8px 16px",
    fontSize: "13px",
    fontWeight: 600,
    // ActiveAdmin's basic-button mixin forces line-height:12px and an embossed
    // text-shadow on plain <button>s, which cramped these labels to the bottom.
    // Inline styles override it for our React buttons.
    lineHeight: 1.2,
    display: "inline-block",
    whiteSpace: "nowrap",
    textShadow: "none",
    boxShadow: "none",
    cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.6 : 1,
  });

  const renderFeeRow = (fee) => (
    <tr key={fee.slug}>
      <td style={td}>
        <div style={{ fontWeight: 600, color: colors.primary }}>
          {fee.name || fee.slug}
          {fee.active === false && (
            <span style={{ marginLeft: "8px", padding: "2px 8px", borderRadius: "12px", fontSize: "11px", fontWeight: 600, color: colors.white, background: colors.gray500 }}>
              Inactive
            </span>
          )}
        </div>
        {fee.description && (
          <div style={{ fontSize: "12px", color: colors.gray500, marginTop: "2px" }}>{fee.description}</div>
        )}
        <div style={{ fontSize: "11px", color: colors.gray500, marginTop: "2px", fontFamily: "monospace" }}>{fee.slug}</div>
      </td>
      <td style={{ ...td, color: colors.gray600 }}>{flowLabel(fee)}</td>
      <td style={{ ...td, fontWeight: 700 }}>{formatValue(fee)}</td>
      <td style={{ ...td, fontSize: "12px", color: colors.gray600 }}>
        <div>{formatWhen(fee.updatedAt)}</div>
        {fee.updatedBy && <div style={{ color: colors.gray500 }}>by {fee.updatedBy}</div>}
      </td>
      <td style={{ ...td, textAlign: "right" }}>
        <button style={btn(colors.accent, false)} onClick={() => openEdit(fee)}>Edit</button>
      </td>
    </tr>
  );

  // A titled fee table card (used per tab / per group).
  const feeCard = ({ title, explainer, action, list, empty }) => (
    <div style={{ background: colors.white, border: `1px solid ${colors.border}`, borderRadius: "10px", overflow: "hidden", marginBottom: "20px" }}>
      {(title || action) && (
        <div style={{ padding: "14px 20px", borderBottom: `1px solid ${colors.border}`, display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "16px", flexWrap: "wrap" }}>
          <div style={{ flex: "1 1 420px" }}>
            {title && <div style={{ fontSize: "14px", fontWeight: 700, color: colors.primary }}>{title}</div>}
            {explainer && <div style={{ fontSize: "12px", color: colors.gray500, marginTop: "3px", lineHeight: 1.5 }}>{explainer}</div>}
          </div>
          {action}
        </div>
      )}
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={th}>Fee</th>
              <th style={th}>Applies to</th>
              <th style={th}>Value</th>
              <th style={th}>Last Updated</th>
              <th style={{ ...th, textAlign: "right" }}>Action</th>
            </tr>
          </thead>
          <tbody>
            {list.length === 0 ? (
              <tr>
                <td colSpan={5} style={{ ...td, textAlign: "center", color: colors.gray500 }}>{empty}</td>
              </tr>
            ) : (
              list.map(renderFeeRow)
            )}
          </tbody>
        </table>
      </div>
    </div>
  );

  // The readable harbor economics table (OwlPay cost → customer pays → markup).
  const harborMarkupCard = (
    <div style={{ background: colors.white, border: `1px solid ${colors.border}`, borderRadius: "10px", overflow: "hidden", marginBottom: "20px" }}>
      <div style={{ padding: "16px 20px", borderBottom: `1px solid ${colors.border}` }}>
        <div style={{ fontSize: "15px", fontWeight: 700, color: colors.primary }}>Harbor fee &amp; our markup (per method)</div>
        <div style={{ fontSize: "12px", color: colors.gray500, marginTop: "4px" }}>
          OwlPay&apos;s cost is invoiced to us monthly; the customer pays the rounded amount and the difference is our markup.
        </div>
      </div>
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={th}>Method</th>
              <th style={{ ...th, textAlign: "right" }}>OwlPay charges us</th>
              <th style={{ ...th, textAlign: "right" }}>Customer pays</th>
              <th style={{ ...th, textAlign: "right" }}><HelpTitle title="Our markup" help="What the customer pays minus what OwlPay charges us." /></th>
            </tr>
          </thead>
          <tbody>
            {harborMarkup.length === 0 ? (
              <tr>
                <td colSpan={4} style={{ ...td, textAlign: "center", color: colors.gray500 }}>No harbor markup data yet.</td>
              </tr>
            ) : (
              [
                ...harborMarkup.map((r, i) => {
                  const markup = Number(r.markup) || 0;
                  const losing = markup < 0;
                  return (
                    <tr key={r.method || i} style={losing ? { background: "#fdecea" } : undefined}>
                      <td style={{ ...td, fontWeight: 600, color: colors.primary }}>{prettyMethod(r.method)}</td>
                      <td style={{ ...td, textAlign: "right" }}>{usd(r.owlpayFee)}</td>
                      <td style={{ ...td, textAlign: "right" }}>{usd(r.customerCharged)}</td>
                      <td style={{ ...td, textAlign: "right", fontWeight: 700, color: losing ? colors.danger : colors.success }}>
                        {usd(r.markup)}
                        {losing ? " ⚠️" : ""}
                      </td>
                    </tr>
                  );
                }),
                (() => {
                  const totalMarkup = harborMarkup.reduce((s, r) => s + (Number(r.markup) || 0), 0);
                  const losing = totalMarkup < 0;
                  return (
                    <tr key="markup-total">
                      <td style={{ ...td, fontWeight: 700 }} colSpan={3}>Total markup</td>
                      <td style={{ ...td, textAlign: "right", fontWeight: 700, color: losing ? colors.danger : colors.success }}>
                        {usd(totalMarkup)}
                      </td>
                    </tr>
                  );
                })(),
              ]
            )}
          </tbody>
        </table>
      </div>
    </div>
  );

  const grouped = {
    platform: fees.filter((f) => providerOf(f) === "platform"),
    owlpay: fees.filter((f) => providerOf(f) === "owlpay"),
    yellowcard: fees.filter((f) => providerOf(f) === "yellowcard"),
    moneygram: fees.filter((f) => providerOf(f) === "moneygram"),
  };
  const owlpayPartner = grouped.owlpay.filter((f) => !isHarborRoundup(f));
  const owlpayHarbor = grouped.owlpay
    .filter(isHarborRoundup)
    .sort((a, b) => (a.slug === HARBOR_PREFIX ? -1 : b.slug === HARBOR_PREFIX ? 1 : (a.slug || "").localeCompare(b.slug || "")));

  return (
    <div style={{ fontFamily: "inherit" }}>
      {/* Tab bar (per provider) + refresh */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: "12px", marginBottom: "16px", borderBottom: `2px solid ${colors.border}` }}>
        <div style={{ display: "flex", gap: "4px", flexWrap: "wrap" }}>
          {TABS.map((t) => {
            const count = grouped[t.key].length;
            const isActive = activeTab === t.key;
            return (
              <button
                key={t.key}
                onClick={() => setActiveTab(t.key)}
                style={{
                  padding: "9px 18px",
                  border: `1px solid ${isActive ? colors.border : "transparent"}`,
                  borderBottom: isActive ? `1px solid ${colors.white}` : "none",
                  borderRadius: "8px 8px 0 0",
                  background: isActive ? colors.white : "transparent",
                  color: isActive ? colors.primary : colors.gray600,
                  fontWeight: isActive ? 700 : 500,
                  fontSize: "14px",
                  cursor: "pointer",
                  marginBottom: "-2px",
                }}
              >
                {t.label}
                {count > 0 && (
                  <span style={{ marginLeft: "6px", background: colors.gray200, color: colors.gray700, borderRadius: "10px", padding: "1px 7px", fontSize: "12px" }}>{count}</span>
                )}
              </button>
            );
          })}
        </div>
        <button style={{ ...btn(colors.primary, loading), marginBottom: "8px" }} onClick={fetchFees} disabled={loading}>
          {loading ? "Loading…" : "↻ Refresh"}
        </button>
      </div>

      {/* Tab content */}
      {loading ? (
        <div style={{ padding: "40px", textAlign: "center", color: colors.gray500 }}>Loading fees…</div>
      ) : (
        <>
          {activeTab === "platform" && feeCard({ list: grouped.platform, empty: "No platform fees configured." })}

          {activeTab === "owlpay" && (
            <>
              {feeCard({
                title: "Partner cost & commission",
                explainer: "What OwlPay charges us (cost) and our commission (revenue) on OwlPay transfers.",
                list: owlpayPartner,
                empty: "No OwlPay partner fees.",
              })}
              {feeCard({
                title: "Harbor Round-up (markup)",
                explainer:
                  "OwlPay's harbor fee is rounded UP to this step when charged to the customer, the difference over OwlPay's actual cost is our revenue. The global step applies to every method; per-method rows override it. Set 0 to disable rounding.",
                action: (
                  <button style={btn(colors.primary, false)} onClick={() => setAddOpen(true)}>+ Add per-method step</button>
                ),
                list: owlpayHarbor,
                empty: "No round-up steps configured yet.",
              })}
              {harborMarkupCard}
            </>
          )}

          {activeTab === "yellowcard" && feeCard({ list: grouped.yellowcard, empty: "No YellowCard fees yet." })}
          {activeTab === "moneygram" && feeCard({ list: grouped.moneygram, empty: "No MoneyGram fees yet." })}
        </>
      )}

      {/* Edit modal */}
      {modal.open && modal.fee && (
        <div
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}
          onClick={closeEdit}
        >
          <div style={{ background: colors.white, borderRadius: "10px", width: "460px", maxWidth: "92vw", overflow: "hidden" }} onClick={(e) => e.stopPropagation()}>
            <div style={{ background: `linear-gradient(135deg, ${colors.accent} 0%, ${colors.accentDark} 100%)`, color: colors.white, padding: "16px 20px", fontWeight: 700 }}>
              Edit fee, {modal.fee.name || modal.fee.slug}
            </div>
            <div style={{ padding: "20px" }}>
              <label style={{ display: "block", fontSize: "13px", fontWeight: 600, color: colors.primary, marginBottom: "6px" }}>Fee type</label>
              <div style={{ display: "inline-flex", border: `1px solid ${colors.border}`, borderRadius: "6px", overflow: "hidden", marginBottom: "16px" }}>
                {[
                  { key: "percent", label: "Percentage" },
                  { key: "flat", label: "Fixed amount" },
                ].map((opt) => (
                  <button
                    key={opt.key}
                    type="button"
                    onClick={() => setForm({ ...form, unit: opt.key })}
                    style={{
                      padding: "8px 18px",
                      border: "none",
                      cursor: "pointer",
                      fontSize: "13px",
                      fontWeight: 600,
                      background: form.unit === opt.key ? colors.accent : colors.white,
                      color: form.unit === opt.key ? colors.white : colors.gray700,
                    }}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>

              <label style={{ display: "block", fontSize: "13px", fontWeight: 600, color: colors.primary, marginBottom: "6px" }}>
                Value {form.unit === "percent" ? "(0-100, e.g. 0.3 = 0.3%)" : `(amount in ${(modal.fee.currency || "USD").toUpperCase()})`}
              </label>
              <div style={{ display: "flex", alignItems: "stretch", marginBottom: "16px", border: `1px solid ${colors.border}`, borderRadius: "6px", overflow: "hidden" }}>
                {form.unit === "flat" && (
                  <span style={{ padding: "10px 12px", background: colors.gray100, color: colors.gray600, borderRight: `1px solid ${colors.border}` }}>
                    {(modal.fee.currency || "USD").toUpperCase() === "USD" ? "$" : (modal.fee.currency || "USD").toUpperCase()}
                  </span>
                )}
                <input
                  type="number"
                  step="0.0001"
                  min="0"
                  max={form.unit === "percent" ? 100 : undefined}
                  value={form.value}
                  onChange={(e) => setForm({ ...form, value: e.target.value })}
                  style={{ flex: 1, padding: "10px", border: "none", outline: "none", boxSizing: "border-box", minWidth: 0 }}
                />
                {form.unit === "percent" && (
                  <span style={{ padding: "10px 12px", background: colors.gray100, color: colors.gray600, borderLeft: `1px solid ${colors.border}` }}>%</span>
                )}
              </div>

              <label style={{ display: "block", fontSize: "13px", fontWeight: 600, color: colors.primary, marginBottom: "6px" }}>Description</label>
              <input
                type="text"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                style={{ width: "100%", padding: "10px", border: `1px solid ${colors.border}`, borderRadius: "6px", marginBottom: "16px", boxSizing: "border-box" }}
              />

              <label style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "14px", color: colors.gray700, marginBottom: "8px", cursor: "pointer" }}>
                <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} />
                Active
              </label>

              <div style={{ background: colors.gray100, borderRadius: "6px", padding: "10px 12px", fontSize: "12px", color: colors.gray600, marginBottom: "16px" }}>
                Changing this fee affects live Agentic Payment charges. The change is audited (who / when).
              </div>

              <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px" }}>
                <button onClick={closeEdit} disabled={saving} style={{ background: colors.gray200, color: colors.gray700, border: "none", borderRadius: "6px", padding: "8px 16px", cursor: "pointer", fontWeight: 600, lineHeight: 1.2, display: "inline-block", textShadow: "none", boxShadow: "none" }}>
                  Cancel
                </button>
                <button onClick={save} disabled={saving} style={btn(colors.accent, saving)}>
                  {saving ? "Saving…" : "Save fee"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add per-method harbor round-up step */}
      {addOpen && (
        <div
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}
          onClick={() => !creating && setAddOpen(false)}
        >
          <div style={{ background: colors.white, borderRadius: "10px", width: "460px", maxWidth: "92vw", overflow: "hidden" }} onClick={(e) => e.stopPropagation()}>
            <div style={{ background: `linear-gradient(135deg, ${colors.primary} 0%, #3d4d7a 100%)`, color: colors.white, padding: "16px 20px", fontWeight: 700 }}>
              Add per-method round-up step
            </div>
            <div style={{ padding: "20px" }}>
              <label style={{ display: "block", fontSize: "13px", fontWeight: 600, color: colors.primary, marginBottom: "6px" }}>Payment method</label>
              <input
                list="owlpay-methods"
                value={addForm.method}
                onChange={(e) => setAddForm({ ...addForm, method: e.target.value })}
                placeholder="e.g. SEPA instant"
                style={{ width: "100%", padding: "10px", border: `1px solid ${colors.border}`, borderRadius: "6px", marginBottom: "6px", boxSizing: "border-box" }}
              />
              <datalist id="owlpay-methods">
                {KNOWN_METHODS.map((m) => (
                  <option key={m} value={m} />
                ))}
              </datalist>
              {addForm.method.trim() && (
                <div style={{ fontSize: "11px", color: colors.gray500, marginBottom: "16px", fontFamily: "monospace" }}>
                  slug: {HARBOR_PREFIX}_{normalizeMethod(addForm.method)}
                </div>
              )}

              <label style={{ display: "block", fontSize: "13px", fontWeight: 600, color: colors.primary, marginBottom: "6px" }}>Round-up step (flat, USD)</label>
              <div style={{ display: "flex", alignItems: "stretch", marginBottom: "16px", border: `1px solid ${colors.border}`, borderRadius: "6px", overflow: "hidden" }}>
                <span style={{ padding: "10px 12px", background: colors.gray100, color: colors.gray600, borderRight: `1px solid ${colors.border}` }}>$</span>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={addForm.value}
                  onChange={(e) => setAddForm({ ...addForm, value: e.target.value })}
                  placeholder="2"
                  style={{ flex: 1, padding: "10px", border: "none", outline: "none", boxSizing: "border-box", minWidth: 0 }}
                />
              </div>

              <label style={{ display: "block", fontSize: "13px", fontWeight: 600, color: colors.primary, marginBottom: "6px" }}>Description (optional)</label>
              <input
                type="text"
                value={addForm.description}
                onChange={(e) => setAddForm({ ...addForm, description: e.target.value })}
                style={{ width: "100%", padding: "10px", border: `1px solid ${colors.border}`, borderRadius: "6px", marginBottom: "16px", boxSizing: "border-box" }}
              />

              <div style={{ background: colors.gray100, borderRadius: "6px", padding: "10px 12px", fontSize: "12px", color: colors.gray600, marginBottom: "16px" }}>
                Overrides the global round-up step for this method. Set 0 to disable rounding for it.
              </div>

              <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px" }}>
                <button onClick={() => setAddOpen(false)} disabled={creating} style={{ background: colors.gray200, color: colors.gray700, border: "none", borderRadius: "6px", padding: "8px 16px", cursor: "pointer", fontWeight: 600, lineHeight: 1.2, display: "inline-block", textShadow: "none", boxShadow: "none" }}>
                  Cancel
                </button>
                <button onClick={submitAdd} disabled={creating} style={btn(colors.primary, creating)}>
                  {creating ? "Adding…" : "Add step"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div
          style={{
            position: "fixed",
            bottom: "24px",
            right: "24px",
            background: toast.type === "danger" ? colors.danger : toast.type === "warning" ? "#e6a23c" : colors.success,
            color: colors.white,
            padding: "12px 18px",
            borderRadius: "8px",
            fontSize: "14px",
            fontWeight: 600,
            boxShadow: "0 4px 16px rgba(0,0,0,0.2)",
            zIndex: 1100,
          }}
        >
          {toast.message}
        </div>
      )}
    </div>
  );
};

export default AgenticPaymentFees;
